import { AdminList, AdminDataGrid, GridColumn } from './shared';
/**
 * Question List Component
 * Displays paginated list of onboarding/questionnaire questions with translation counts
 */

import {
  TextField,
  NumberField,
  SearchInput,
  SelectInput,
  useRecordContext,
} from 'react-admin';
import { Chip, Box, Tooltip } from '@mui/material';
import { semantic } from '@rekindle/shared/theme';
import TranslateIcon from '@mui/icons-material/Translate';
import { ListActions } from './shared';
import { QUESTION_TIER_CHOICES, QUESTION_ASK_AT_CHOICES } from '../constants';

/**
 * Custom field to display tier with color coding
 */
function TierField() {
  const record = useRecordContext();
  if (!record?.tier) return null;

  const tier = record.tier;
  const color = tier === 1 ? semantic.info : semantic.warning;
  const label = tier === 1 ? 'Tier 1 (Onboarding)' : 'Tier 2 (Later)';

  return (
    <Chip
      label={label}
      size="small"
      sx={{
        backgroundColor: color,
        color: 'white',
      }}
    />
  );
}

/**
 * Custom field to display askAt timing
 */
function AskAtField() {
  const record = useRecordContext();
  if (!record?.askAt) return null;

  return (
    <Chip
      label={record.askAt}
      size="small"
      variant="outlined"
      sx={{ textTransform: 'capitalize' }}
    />
  );
}

/**
 * Custom field to display translation count with icon
 */
function TranslationCountField() {
  const record = useRecordContext();
  if (record?.translationCount === undefined) return null;

  const count = record.translationCount;
  const color = count > 0 ? semantic.success : semantic.neutral;

  return (
    <Tooltip title={`${count} translation(s) available`}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <TranslateIcon sx={{ fontSize: 16, color }} />
        <Chip
          label={count}
          size="small"
          sx={{
            backgroundColor: color,
            color: 'white',
            minWidth: 32,
          }}
        />
      </Box>
    </Tooltip>
  );
}

/**
 * Filters for question list
 */
const questionFilters = [
  <SearchInput key="q" source="q" alwaysOn placeholder="Search questions..." />,
  <SelectInput
    key="tier"
    source="tier"
    label="Tier"
    choices={QUESTION_TIER_CHOICES}
  />,
  <SelectInput
    key="askAt"
    source="askAt"
    label="Ask At"
    choices={QUESTION_ASK_AT_CHOICES}
  />,
];

export function QuestionList() {
  return (
    <AdminList
      filters={questionFilters}
      actions={<ListActions hasCreate={false} />}
      sort={{ field: 'tier', order: 'ASC' }}
      perPage={25}
    >
      <AdminDataGrid bulkActionButtons={false} rowClick="show">
        <TextField source="id" label="ID" />
        <TextField source="text" label="Question Text" />
        <GridColumn label="Tier" source="tier"><TierField /></GridColumn>
        <GridColumn label="Ask at" source="askAt"><AskAtField /></GridColumn>
        <TextField source="contextField" label="Context Field" />
        <NumberField source="optionCount" label="Options" />
        <GridColumn label="Translations" source="translationCount"><TranslationCountField /></GridColumn>
      </AdminDataGrid>
    </AdminList>
  );
}
